import React, { useMemo } from "react"
import { Tab, Tabs } from "react-bootstrap"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { connect } from "scrivito"
import { useControlledState } from "shared/lib/useControlledState"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { ProductPositionFormTabType } from "features/product-position-management/types/ProductPositionFormTabType"

import { useProductPositionFormAttachmentsContext } from "../context/ProductPositionFormAttachmentsContext"
import { useProductPositionFormCoreContext } from "../contexts/ProductPositionFormCoreContext"
import { useProductPositionFormFeaturesContext } from "../contexts/ProductPositionFormFeaturesContext"
import { useProductPositionFormProductInformationContext } from "../contexts/ProductPositionFormProductInformationContext"
import { ProductPositionAttachmentsTab } from "./ProductPositionAttachmentsTab"
import { ProductPositionFormAddonsTab } from "./ProductPositionFormAddonsTab"
import { ProductPositionFormClarificationRequestTab } from "./ProductPositionFormClarificationRequestTab"
import { ProductPositionFormConfigurationTab } from "./ProductPositionFormConfigurationTab"
import { ProductPositionFormDimensioningTab } from "./ProductPositionFormDimensioningTab"
import { ProductPositionFormDownloadsTab } from "./ProductPositionFormDownloadsTab"
import { ProductPositionFormGeneralTab } from "./ProductPositionFormGeneralTab"
import { ProductPositionFormInstallationReferencesTab } from "./ProductPositionFormInstallationReferencesTab"
import { ProductPositionFormRemarkTab } from "./ProductPositionFormRemarkTab"
import { ProductPositionFormTenderTextTab } from "./ProductPositionFormTenderTextTab"

interface ProductPositionFormTabsProps {
  defaultTab?: ProductPositionFormTabType
  generalTabComponent?: React.ComponentType
  remarkTabComponent?: React.ComponentType
  clarificationRequestTabComponent?: React.ComponentType
  configurationTabComponent?: React.ComponentType
  tenderTextTabComponent?: React.ComponentType
  dimensioningTabComponent?: React.ComponentType
  addonsTabComponent?: React.ComponentType
  downloadsTabComponent?: React.ComponentType
  installationReferencesTabComponent?: React.ComponentType
  attachmentsTabComponent?: React.ComponentType
}

function ProductPositionFormTabsComponent({
  defaultTab,
  generalTabComponent: GeneralTabContent = ProductPositionFormGeneralTab,
  remarkTabComponent: RemarkTabContent = ProductPositionFormRemarkTab,
  clarificationRequestTabComponent:
    ClarificationRequestTabContent = ProductPositionFormClarificationRequestTab,
  configurationTabComponent:
    ConfigurationTabContent = ProductPositionFormConfigurationTab,
  tenderTextTabComponent:
    TenderTextTabContent = ProductPositionFormTenderTextTab,
  attachmentsTabComponent:
    AttachmentsTabContent = ProductPositionAttachmentsTab,
  dimensioningTabComponent:
    DimensioningTabContent = ProductPositionFormDimensioningTab,
  addonsTabComponent: AddonsTabContent = ProductPositionFormAddonsTab,
  downloadsTabComponent: DownloadsTabContent = ProductPositionFormDownloadsTab,
  installationReferencesTabComponent:
    InstallationReferencesTabContent = ProductPositionFormInstallationReferencesTab
}: ProductPositionFormTabsProps) {
  const { t } = useTranslation()
  const { isReadonly, formOptions, permissions } =
    useProductPositionFormCoreContext()
  const {
    hasClarificationRequest,
    installationReferences,
    openInstallationReferenceSelection
  } = useProductPositionFormFeaturesContext()
  const { productInformation, getFilesCallback } =
    useProductPositionFormProductInformationContext()
  const {
    hideClarificationRequest,
    hideRemark,
    hideAttachments,
    activeTab,
    onTabChange
  } = formOptions || {}
  const { linkedAttachments } = useProductPositionFormAttachmentsContext()

  const remark = useWatch({ name: "productPosition.remark" })
  const references = useWatch({ name: "productPosition.references" })

  const showRemarkTab = !hideRemark && (!isReadonly || !!remark)
  const showCRTab =
    !hideClarificationRequest && (!isReadonly || hasClarificationRequest)
  const showConfigurationTab =
    isReadonly && !!productInformation?.attributes?.length
  const showTenderTextTab =
    isReadonly &&
    (!!productInformation?.generalDescription ||
      !!productInformation?.variantDescription)
  const showAttachmentsTab =
    !hideAttachments && ((linkedAttachments?.length ?? 0) > 0 || !isReadonly)
  const showDimensioningTab = isReadonly && !!productInformation?.dimensioning
  const showAddonsTab = isReadonly && !!productInformation?.addons?.length
  const showDownloadsTab =
    isReadonly &&
    !!getFilesCallback &&
    !!productInformation &&
    (!!getFilesCallback({ productType: productInformation.productType })
      ?.length ||
      !!productInformation.addons?.some(
        addon =>
          getFilesCallback({
            productType: addon.productType,
            parentProductType: productInformation.productType
          })?.length
      ))
  const showInstallationReferencesTab = isReadonly
    ? (!!openInstallationReferenceSelection &&
        permissions[ProjectPermissionType.EDIT]) ||
      (installationReferences?.length ?? 0) > 0
    : (references?.length ?? 0) > 0

  const [effectiveTab, handleTabChange] =
    useControlledState<ProductPositionFormTabType>({
      value: activeTab,
      defaultValue: defaultTab ?? ProductPositionFormTabType.GENERAL,
      onChange: onTabChange
    })

  const resolvedTab = useMemo(() => {
    const visibleTabs = new Set<ProductPositionFormTabType>([
      ProductPositionFormTabType.GENERAL,
      ...(showCRTab ? [ProductPositionFormTabType.CLARIFICATION_REQUEST] : []),
      ...(showDimensioningTab ? [ProductPositionFormTabType.DIMENSIONING] : []),
      ...(showConfigurationTab
        ? [ProductPositionFormTabType.CONFIGURATION]
        : []),
      ...(showAddonsTab ? [ProductPositionFormTabType.ADDONS] : []),
      ...(showRemarkTab ? [ProductPositionFormTabType.REMARK] : []),
      ...(showInstallationReferencesTab
        ? [ProductPositionFormTabType.INSTALLATION_REFERENCES]
        : []),
      ...(showDownloadsTab ? [ProductPositionFormTabType.DOWNLOADS] : []),
      ...(showTenderTextTab ? [ProductPositionFormTabType.TENDER_TEXT] : []),
      ...(showAttachmentsTab ? [ProductPositionFormTabType.ATTACHMENTS] : [])
    ])
    return visibleTabs.has(effectiveTab)
      ? effectiveTab
      : ProductPositionFormTabType.GENERAL
  }, [
    effectiveTab,
    showCRTab,
    showDimensioningTab,
    showConfigurationTab,
    showAddonsTab,
    showRemarkTab,
    showInstallationReferencesTab,
    showDownloadsTab,
    showTenderTextTab,
    showAttachmentsTab
  ])

  if (
    !showRemarkTab &&
    !showCRTab &&
    !showConfigurationTab &&
    !showTenderTextTab &&
    !showDimensioningTab &&
    !showAddonsTab &&
    !showDownloadsTab &&
    !showInstallationReferencesTab &&
    !showAttachmentsTab
  ) {
    return <GeneralTabContent />
  }

  return (
    <Tabs
      activeKey={resolvedTab}
      onSelect={key =>
        handleTabChange(
          (key as ProductPositionFormTabType) ??
            ProductPositionFormTabType.GENERAL
        )
      }
      className="product-position-form__tabs"
    >
      <Tab
        eventKey={ProductPositionFormTabType.GENERAL}
        title={t("ProductPositionForm.Tab.GENERAL")}
      >
        <GeneralTabContent />
      </Tab>
      {showCRTab && (
        <Tab
          eventKey={ProductPositionFormTabType.CLARIFICATION_REQUEST}
          title={
            <span
              className={classNames({
                "nav-link__content--warning": hasClarificationRequest
              })}
            >
              {hasClarificationRequest && (
                <Icon
                  name={IconName.CLARIFICATION_REQUEST}
                  size={IconSize.SMALL}
                />
              )}
              {t("ClarificationRequest.Form.Tab.Label")}
            </span>
          }
        >
          <ClarificationRequestTabContent />
        </Tab>
      )}
      {showDimensioningTab && (
        <Tab
          eventKey={ProductPositionFormTabType.DIMENSIONING}
          title={t("ProductPositionForm.Tab.DIMENSIONING")}
        >
          <DimensioningTabContent />
        </Tab>
      )}
      {showConfigurationTab && (
        <Tab
          eventKey={ProductPositionFormTabType.CONFIGURATION}
          title={t("ProductPositionForm.Tab.CONFIGURATION")}
        >
          <ConfigurationTabContent />
        </Tab>
      )}
      {showAddonsTab && (
        <Tab
          eventKey={ProductPositionFormTabType.ADDONS}
          title={t("ProductPositionForm.Tab.ADDONS")}
        >
          <AddonsTabContent />
        </Tab>
      )}
      {showRemarkTab && (
        <Tab
          eventKey={ProductPositionFormTabType.REMARK}
          title={t("ProductPositionForm.Tab.REMARK")}
        >
          <RemarkTabContent />
        </Tab>
      )}
      {showInstallationReferencesTab && (
        <Tab
          eventKey={ProductPositionFormTabType.INSTALLATION_REFERENCES}
          title={t("ProductPositionForm.Tab.INSTALLATION_REFERENCES")}
        >
          <InstallationReferencesTabContent />
        </Tab>
      )}
      {showDownloadsTab && (
        <Tab
          eventKey={ProductPositionFormTabType.DOWNLOADS}
          title={t("ProductPositionForm.Tab.DOWNLOADS")}
        >
          <DownloadsTabContent />
        </Tab>
      )}
      {showTenderTextTab && (
        <Tab
          eventKey={ProductPositionFormTabType.TENDER_TEXT}
          title={t("ProductPositionForm.Tab.TENDER_TEXT")}
        >
          <TenderTextTabContent />
        </Tab>
      )}

      {showAttachmentsTab && (
        <Tab
          eventKey={ProductPositionFormTabType.ATTACHMENTS}
          title={t("ProductPositionForm.Tab.ATTACHMENTS")}
        >
          <AttachmentsTabContent />
        </Tab>
      )}
    </Tabs>
  )
}

export const ProductPositionFormTabs = React.memo(
  connect(ProductPositionFormTabsComponent)
)
